"use client"

import { useState, useRef, useEffect } from "react"
import Link from "next/link"
import { Menu, TitleNameSlug } from "../../@types/Menu"
import { useFloating, offset, flip, shift } from "@floating-ui/react"

interface DropdownProps {
  menu: Menu
  hasSubmenu?: boolean
  position?: "left" | "right"
}

export default function Dropdown({ menu, hasSubmenu = false, position = "left" }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Use Floating UI for positioning
  const { refs, floatingStyles } = useFloating({
    placement: position === "left" ? "bottom-start" : "bottom-end",
    middleware: [offset(5), flip(), shift()],
  })

  // Helper function to generate href
  const generateHref = (item: TitleNameSlug) => {
    if (item.link) return item.link

    return `/${menu.slug}/${item.slug}`
  }

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 100) // Small delay to prevent flickering
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Simple link if no submenu
  if (!hasSubmenu) {
    return (
      <Link
        href={menu.slug}
        className="group inline-flex items-center gap-x-1 leading-6 text-gray-900 hover:text-yellow-500 py-4"
      >
        <span className="uppercase">{menu.name}</span>
      </Link>
    )
  }

  // Get submenu items
  const submenuItems = Object.entries(menu)
    .filter(([key, value]) => Array.isArray(value))
    .flatMap(([key, value]) => value as TitleNameSlug[])

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="relative">
      {/* Trigger button */}
      <button
        ref={refs.setReference}
        className="group inline-flex items-center gap-x-1 leading-6 text-gray-900 py-4 whitespace-nowrap"
      >
        <Link
          href={`/${menu.slug}`}
          className="uppercase text-ellipsis overflow-hidden hover:text-yellow-500"
          onClick={() => setIsOpen(false)}
        >
          {menu.name}
        </Link>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div ref={refs.setFloating} style={floatingStyles} className="z-50 min-w-[120px] max-w-[800px]">
          <div className="w-full h-[5px] bg-matco"></div>
          <div className="bg-white shadow-lg min-w-[120px] max-w-[800px]">
            <div className="py-2 w-max">
              {submenuItems.map((item, index) => (
                <Link
                  key={index}
                  href={generateHref(item)}
                  className="block py-2 px-4 text-sm text-gray-900 hover:text-black hover:bg-gray01 uppercase text-left whitespace-pre-wrap break-words [@media(min-width:1024px)_and_(max-width:1099px)]:text-[12px] [@media(min-width:1100px)_and_(max-width:1200px)]:text-[13px] [@media(min-width:1201px)_and_(max-width:1325px)]:text-[15px]"
                  onClick={() => setIsOpen(false)}
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
